import { Box, Button, Icon, Tag, Text } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import 'modules/billing/components/FactorialF25Bundles/index.styles.css'
import { formatPrice } from 'modules/billing/lib/f25Prices'
import { useF25PricingContext } from 'modules/onboarding/components/Pricing/F25PricingContext'
import type Plan from 'modules/onboarding/lib/pricing/plans/Plan'
import { DEFAULT_CURRENCY } from 'modules/onboarding/lib/pricing/plans/utils'
import { PeriodUnit } from 'modules/onboarding/lib/pricing/types'

type PriceInfo = {
  plan: Plan
  period: PeriodUnit
  currency: string
  country: string
}

const PriceInfo = ({ plan, period, currency, country }: PriceInfo) => {
  const { seats } = useF25PricingContext()
  const perSeatPrice = plan.pricing.getSeatsPrice(period)
  const feePrice = plan.pricing.getFeePrice(period)
  const stairstepPrice = plan.pricing.getStairstepPrice(period, seats)
  const tieredPrice = plan.pricing.getTieredPrice(period)
  // The first tier of a plan have a price per seat of 0. We can take the endingUnit of this tier to figure out the starting seats.
  const includedSeatsPerModule = tieredPrice?.[0]?.endingUnit
  // The next tier is where we can find the price per seat of all those seats above the "starting seats".
  const pricePerUser = tieredPrice?.[1]?.price || 0

  const EXCLUDED_COUNTRIES = ['br']
  const excludedCountries = EXCLUDED_COUNTRIES.includes(country)

  return (
    <Box paddingTop="s8" flexDirection="row" gap="s4">
      {perSeatPrice ? (
        <>
          <Text
            size="200"
            color="grey1000"
            weight="semibold"
            className={excludedCountries ? 'FactorialF25Bundles_blurredText' : ''}
          >
            {formatPrice(perSeatPrice || 0, currency)}
          </Text>
          <Text size="200" color="grey700">
            {i18n.t('pricing_plans.per_employee_month')}
          </Text>
        </>
      ) : null}
      {feePrice ? (
        <>
          <Text
            size="200"
            color="grey1000"
            weight="semibold"
            className={excludedCountries ? 'FactorialF25Bundles_blurredText' : ''}
          >
            {formatPrice(feePrice || 0, currency)}
          </Text>
          <Text size="200" color="grey700">
            {i18n.t('pricing_plans.fixed_fee')}
            {includedSeatsPerModule && (
              <>
                ,{' '}
                {i18n.t('pricing_plans.employees_inludede', {
                  employees: includedSeatsPerModule,
                })}
                {tieredPrice && (
                  <Text.Normal size="200" color="grey700">
                    ,{' '}
                    {i18n.t('pricing_plans.per_new_employee', {
                      amount: formatPrice(pricePerUser / 100, currency),
                    })}
                  </Text.Normal>
                )}
              </>
            )}
          </Text>
        </>
      ) : null}
      {stairstepPrice ? (
        <>
          <Text
            size="200"
            color="grey1000"
            weight="semibold"
            className={excludedCountries ? 'FactorialF25Bundles_blurredText' : ''}
          >
            {formatPrice(stairstepPrice || 0, currency)}
          </Text>
          <Text size="200" color="grey700">
            {i18n.t('pricing_plans.fixed_fee')}
          </Text>
        </>
      ) : null}
    </Box>
  )
}

const ModuleCard = ({ plan, isRecommended }: { plan: Plan; isRecommended: boolean }) => {
  const { allPlans, period, togglePlanSelect, country } = useF25PricingContext()

  const currency = allPlans.f25Plans[0]?.pricing?.currency || DEFAULT_CURRENCY
  const isSelected = plan.isSelected
  const buttonLabel = isSelected
    ? i18n.t('pricing_plans.added_module')
    : i18n.t('pricing_plans.add_module')

  return (
    <Box
      border={{ all: { color: 'grey400', width: 's1', style: 'solid' } }}
      borderRadius={{ all: 'abs008' }}
      padding="s16"
      gap="s16"
      paddingRight="s24"
      flexDirection="row"
      justifyContent="spaceBetween"
    >
      <Box cursor="pointer" flexDirection="row" onClick={() => {}} gap="s8">
        <Box alignSelf="start" style={{ flex: '1' }}>
          <Icon.Medium icon={plan.icon} color="grey800" />
        </Box>
        <Box gap="s8">
          <Box flexDirection="row" gap="s8" flexWrap="wrap">
            <Text size="300" weight="semibold" color="grey1000">
              {plan.title}
            </Text>
            {isRecommended && <Tag type="viridian">{i18n.t('pricing_plans.recommended_tag')}</Tag>}
          </Box>
          <Box maxWidth="s400">
            <Text size="200" color="grey700">
              {plan.description}
            </Text>
          </Box>
          <PriceInfo plan={plan} period={period} currency={currency} country={country} />
        </Box>
      </Box>
      <Button onClick={() => togglePlanSelect(plan)} type={isSelected ? 'positive' : 'secondary'}>
        {buttonLabel}
      </Button>
    </Box>
  )
}

export default ModuleCard
